export const isPlainObject = (value) =>
  value != null && typeof value === 'object' && !Array.isArray(value)

/**
 * Walks the React Query infinite-data cache for ['libraryItems', 'sessions', ...]
 * queries, finds the row matching `sessionId`, and replaces its
 * `attributes.mainRun` with the result of `transform(prevMainRun)`.
 *
 * Pure plumbing: the primitive owns the cache predicate, the page walk, and
 * the item identity check. Each caller supplies a `transform` that decides
 * how `mainRun` is merged (shallow vs nested vs anything else). Does not
 * refetch.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {{ sessionId: string|number, transform: (prevMainRun: object) => object }} params
 */
export function patchSessionMainRunInCache(queryClient, { sessionId, transform } = {}) {
  if (!queryClient?.setQueriesData || sessionId == null || typeof transform !== 'function') {
    return
  }

  const sessionKey = String(sessionId)

  queryClient.setQueriesData(
    {
      predicate: (query) => Array.isArray(query.queryKey)
        && query.queryKey[0] === 'libraryItems'
        && query.queryKey[1] === 'sessions',
    },
    (oldData) => {
      if (oldData == null || !Array.isArray(oldData.pages)) {
        return oldData
      }

      return {
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: (page.items || []).map((item) => {
            if (item == null || String(item.id) !== sessionKey) {
              return item
            }

            const attributes = item.attributes || {}
            const prevMainRun = isPlainObject(attributes.mainRun) ? attributes.mainRun : {}

            return {
              ...item,
              attributes: {
                ...attributes,
                mainRun: transform(prevMainRun),
              },
            }
          }),
        })),
      }
    },
  )
}
